import { useEffect, useState, MouseEvent } from "react";
import { Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useSetRecoilState } from "recoil";
import { BiX } from "react-icons/bi";

import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";
import { useResolvedPaymentProvider } from "MVVM/Hooks/useResolvedPaymentProvider";
import { StripePaystationState } from "MVVM/State";
import { TIER_PRICING } from "MVVM/Utilities/SubscriptionTiers";
import { getQueryParam } from "MVVM/Utilities/QueryParam";

const LOCAL_STORAGE_KEY = "unlimited-upgrade-seen";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

// Discount depth scales inversely with what the user already pays: the jump
// from Basic to Unlimited is 12x their spend, so they get the deeper cut.
const UPGRADE_OFFERS: Record<string, { coupon: string; percent: number }> = {
  ESSENTIALS: { coupon: "upgrade75", percent: 75 },
  PRO: { coupon: "upgrade50", percent: 50 },
};

/**
 * One-shot upgrade pitch to Unlimited for active monthly Basic/Standard
 * subscribers. Monthly-only (annual holders are mid-commitment and would hit
 * proration weirdness), and at least a week into their current cycle so a
 * brand-new subscriber isn't immediately upsold past the tier they just
 * chose. The annual modal owns Unlimited members; this one owns the tiers
 * below it, so the two never compete for the same viewer.
 */
const UnlimitedUpgradeUpsell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { allstarUser } = useUser();
  const setPaystation = useSetRecoilState(StripePaystationState);
  const { provider, resolve: resolveProvider } = useResolvedPaymentProvider();

  useEffect(() => {
    resolveProvider();
  }, [resolveProvider]);

  const userGroup = allstarUser?.user?.userGroup;
  const loggedIn = allstarUser?.loggedIn;
  const existingProvider = allstarUser?.user?.credits?.providerName;
  // Coupon and checkout flow are Stripe-only.
  const isXsollaUser = existingProvider === "XSOLLA" || provider === "xsolla";

  // Dev-only harness: `?debugUpgradeModal=1` pops the modal with no
  // eligibility, login, or dismissal guard (same pattern as WinbackUpsell).
  const debug =
    process.env.NODE_ENV === "development" &&
    getQueryParam("debugUpgradeModal") === "1";

  const offer = UPGRADE_OFFERS[debug ? "PRO" : userGroup?.type ?? ""];

  useEffect(() => {
    if (debug) {
      setIsOpen(true);
      return;
    }
    if (!loggedIn || !userGroup || !offer) return;
    if (isXsollaUser) return;
    const { active, paymentDate, expires } = userGroup;

    if (localStorage.getItem(LOCAL_STORAGE_KEY)) return;
    if (!active) return;
    if (!paymentDate || !expires) return;

    const paymentMs = Number(paymentDate);
    const expiresMs = Number(expires);
    if (isNaN(paymentMs) || isNaN(expiresMs)) return;
    if (expiresMs - paymentMs >= SIXTY_DAYS_MS) return;
    if (Date.now() - paymentMs < SEVEN_DAYS_MS) return;

    analytics.track("Unlimited Upgrade Offer - Viewed", {
      location: "Modal",
      fromTier: userGroup.type,
    });
    setIsOpen(true);
  }, [debug, loggedIn, userGroup, offer, isXsollaUser]);

  if (!isOpen || !offer) return <></>;

  const dismiss = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    if (debug) return;
    analytics.track("Unlimited Upgrade Offer - Dismissed", {
      location: "Modal",
      fromTier: userGroup?.type,
    });
  };

  const handleUpgrade = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    if (!debug) {
      analytics.track("Unlimited Upgrade Offer - Checkout Clicked", {
        location: "Modal",
        fromTier: userGroup?.type,
      });
    }
    setPaystation({
      isModalOpen: true,
      type: "subscribe",
      lookUp: "plat_monthly",
      couponId: offer.coupon,
      pendingEmail: false,
      shouldRefreshMtx: false,
    });
  };

  return (
    <ModalOverlay onClick={dismiss}>
      <ModalContainer onClick={(e: MouseEvent) => e.stopPropagation()}>
        <CloseButton onClick={dismiss}>
          <BiX size={24} />
        </CloseButton>
        <CardHeader>
          <OfferBadge>Upgrade offer</OfferBadge>
          <Title>Try Unlimited, {offer.percent}% off</Title>
        </CardHeader>
        <ModalContent>
          <Blurb>
            No clip limits, no caps, every Unlimited perk. Get your first month
            at {offer.percent}% off, then {TIER_PRICING.CREATOR_PLAT.monthly}
            /mo. Cancel anytime.
          </Blurb>
          <UpgradeButton onClick={handleUpgrade}>
            Upgrade to Unlimited, {offer.percent}% off
          </UpgradeButton>
          <SecondaryButton onClick={dismiss}>Maybe later</SecondaryButton>
          <FinePrint>Discount applies to your first month.</FinePrint>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const ModalContainer = styled(Box)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.tierPlatAlpha40};
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.25),
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 0 24px ${({ theme }) => theme.colors.tierPlatAlpha20};
  max-width: 480px;
  overflow: hidden;
  position: relative;
  width: 90%;
`;

const CardHeader = styled(Box)`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.tierPlatAlpha25} 0%,
    transparent 60%
  );
  border-radius: 16px 16px 0 0;
  padding: 28px 24px 20px;
`;

const OfferBadge = styled(Box)`
  background: ${({ theme }) => theme.colors.tierPlatAlpha15};
  border: 1px solid ${({ theme }) => theme.colors.tierPlatAlpha40};
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.tierPlat};
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  padding: 3px 8px;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: color 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
`;

const ModalContent = styled(Box)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 24px;
`;

const Blurb = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  line-height: 1.5;
`;

const UpgradeButton = styled.button`
  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.tierPlat} 0%,
    ${({ theme }) => theme.colors.tierPlatDark} 100%
  );
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 11px 16px;
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(1.1);
  }
`;

const SecondaryButton = styled.button`
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  padding: 9px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.25);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const FinePrint = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 11.5px;
  margin-top: 2px;
  text-align: center;
`;

export { UnlimitedUpgradeUpsell };
